// @ts-ignore
import restrictScroll from 'restrict-scroll';
import { closest } from '../../utils/domHelpers';
import { OpenedDialogs, notifyOfBackdropChange } from './helpers';
import { directions } from './types';

export interface StyleObj {
  [key: string]: string;
}

export const nonDisplayableElementTagNames = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);

// Watch for when the style attribute of an `<aha-dialog>` changes and apply the outer `<aha-dialog>`'s styles to the `#inner` dialog. This way we can change styles of the `<aha-dialog>` and have it reflected in the inner dialog.
export const styleMutationObs = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    const host = mutation.target as HTMLAhaDialogElement;
    // @ts-ignore
    applyStyle.call(host, inlineStyleToObject(host));
  });
});

// Turn a "style" attribute into an object suitable for merging with an element's existing style object.
export function inlineStyleToObject(elem: HTMLElement) {
  return Array.from(elem.style).reduce((acc, key) => {
    const prop = elem.style.getPropertyValue(key);
    const importance = elem.style.getPropertyPriority(key);
    const value = `${prop} ${importance ? `!${importance}` : ''}`.trim();
    acc[key] = value;
    return acc;
  }, {} as Record<string, any>);
}

/** Apply an object with various style rules (ex. {top: '40px'}) to inner dialog. */
export function applyStyle(this: any, newStyle: Record<string | number | symbol, any>) {
  if (this.innerElem && newStyle) {
    const existingStyleAttrs = Object.keys(this.innerElem.getAttribute('style') || {}).filter(
      // Ensure that no keys which start with a number are added. This can happen when using Dev Tools and adjusting values manually.
      key => !/^\d+$/.test(key),
    );

    const styles: Record<string | number | symbol, any> = Object.entries(newStyle).reduce(
      (acc, [key, value]: Array<any>) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, any>,
    );

    // Ensure that any style attributes that exist on the old style but do not exist on the new style are properly unset when the style objects are merged.
    existingStyleAttrs.forEach(attr => {
      if (!styles[attr]) styles[attr] = '';
    });
    Object.assign(this.innerElem.style, styles);
  }
}

// Watch the size of the innerElem and reposition when it changes.
export const resizeObserver = new ResizeObserver(entries => {
  entries.forEach(entry => {
    const innerElem = entry.target as HTMLDivElement;
    const shadowRoot = innerElem.getRootNode() as ShadowRoot;
    const host = shadowRoot.host as HTMLAhaDialogElement;
    // @ts-ignore
    if (host.open && host.isRelativelyPositioned) {
      host.reposition();
    }
  });
});

// All of the data that is potentially passed by a PointerEvent (which includes MouseEvents).
export const pointerEventOptions = () =>
  Object.assign(Object.create(null), {
    isTrusted: undefined,
    altKey: undefined,
    altitudeAngle: undefined,
    azimuthAngle: undefined,
    bubbles: undefined,
    button: undefined,
    buttons: undefined,
    cancelBubble: undefined,
    cancelable: undefined,
    clientX: undefined,
    clientY: undefined,
    composed: undefined,
    ctrlKey: undefined,
    currentTarget: undefined,
    defaultPrevented: undefined,
    detail: undefined,
    eventPhase: undefined,
    fromElement: undefined,
    height: undefined,
    isPrimary: undefined,
    layerX: undefined,
    layerY: undefined,
    metaKey: undefined,
    movementX: undefined,
    movementY: undefined,
    offsetX: undefined,
    offsetY: undefined,
    pageX: undefined,
    pageY: undefined,
    pointerId: undefined,
    pointerType: undefined,
    pressure: undefined,
    relatedTarget: undefined,
    returnValue: undefined,
    screenX: undefined,
    screenY: undefined,
    shiftKey: undefined,
    sourceCapabilities: undefined,
    srcElement: undefined,
    tangentialPressure: undefined,
    target: undefined,
    tiltX: undefined,
    tiltY: undefined,
    toElement: undefined,
    twist: undefined,
    type: undefined,
    view: undefined,
    which: undefined,
    width: undefined,
    x: undefined,
    y: undefined,
  });

export function handleOpen(this: any) {
  if (this.open) {
    if (this.dialogElem && !this.dialogElem.hasAttribute('open')) {
      // Set reset style.
      if (!this.stylesBeforeOpened) this.stylesBeforeOpened = this.host.getAttribute('style') || '';

      this.closing = false;
      this.cancelled = false;
      OpenedDialogs.add(this.host);
      if (this.restrict) restrictScroll.add(this.host);
      this.openEvent.emit(this);
      // @ts-ignore
      this.dialogElem.showModal();
      this.opening = true;
      if (this.isRelativelyPositioned) this.reposition();
    }
  } else {
    this.closeEvent.emit(this);
    this.opening = false;
    this.closing = true;
    OpenedDialogs.delete(this.host);
    restrictScroll.delete(this.host);
    // NOTE: Closing the modal is actually handled by the `handleAnimationEnd` event, so that we can also fade out the lifted element.
  }
}

export function updateBackdrops() {
  // Notify all <aha-dialog> elements that the dialog with the visible backdrop may have changed.
  notifyOfBackdropChange();
}

export function addScrollListeners(this: any) {
  // Add scroll listeners to all the parent elements of the open <aha-dialog>, as well as the window.
  let parentElem = this.host.parentElement;

  while (parentElem && parentElem.tagName !== 'HTML') {
    if (this.open) {
      parentElem.addEventListener('scroll', this.scrollEvent);
    } else {
      parentElem.removeEventListener('scroll', this.scrollEvent);
    }
    parentElem = parentElem.parentElement;
  }

  if (this.open) {
    window.addEventListener('scroll', this.scrollEvent);
  } else {
    window.removeEventListener('scroll', this.scrollEvent);
  }
}

export function handlePositionChange(this: any) {
  if (this.position === 'relative') {
    setPositionTarget.call(this);
  } else {
    this.positionTarget = undefined;
  }

  if (this.open) {
    // If the position is set to any of the `center` values, then unset the appropriate top/bottom/left/right values to make things work. Values should be set to `null` to remove the key from the style object.
    if (this.position === 'center') {
      applyStyle.call(this, { top: null, bottom: null, left: null, right: null });
    } else if (this.position === 'h-center') {
      applyStyle.call(this, { left: null, right: null });
    } else if (this.position === 'v-center') {
      applyStyle.call(this, { top: null, bottom: null });
    } else if (this.isRelativelyPositioned && this.innerElem) {
      if (!this.directions) this.directions = 'auto';
      this.reposition();
    }
  }
}

export function sanitizeDirections(this: any, value: string, oldValue: string, attr: string) {
  if (value !== oldValue) buildPossibleDirections.call(this);
}

// Expand the 'auto' direction to cover all possible placements.
export function buildPossibleDirections(this: any) {
  if (!this.directions) {
    this.directions = 'auto';
  }
  const possibleDirections = new Set(this.directions.split(/[^a-z\-]+/));
  if (possibleDirections.has('auto')) {
    possibleDirections.delete('auto');
    directions.forEach(direction => possibleDirections.add(direction));
  }
  this.possibleDirections = possibleDirections;
}

export function handleRestrict(this: any) {
  if (this.restrict) {
    restrictScroll.add(this.host);
  } else {
    restrictScroll.delete(this.host);
  }
}

export function emitCancelEvent(this: any) {
  this.cancelEvent.emit(this);
}

export function toggle(this: any) {
  this.open = !this.open;
}

export function show(this: any) {
  this.open = true;
}

export function hide(this: any, immediate?: boolean) {
  if (immediate) {
    const tagName = this.host.tagName.toLowerCase();

    this.dialogElem.style.setProperty(`--${tagName}--fade-out-duration`, '0s');
    this.host.addEventListener(
      'aha-dialog:closed',
      (e: any) => {
        this.dialogElem.style.removeProperty(`--${tagName}--fade-out-duration`);
      },
      { once: true },
    );
  }

  this.open = false;
}

export function bringToFront(this: any) {
  if (this.open && !this.maximal) {
    // Immediately close and then re-open the dialog without waiting for any animations, etc.
    queueMicrotask(() => {
      if (this.dialogElem) {
        // @ts-ignore
        this.dialogElem.close();
        OpenedDialogs.add(this.host);
        // @ts-ignore
        this.dialogElem.showModal();
      }
    });
  }
}

export function scrollEvent(this: any, e: Event) {
  if (this.open && this.isRelativelyPositioned) this.reposition();
}

export function handleCancel(this: any, e: Event) {
  e.preventDefault();
  const dialog = e.target as HTMLDialogElement;
  const root = dialog.getRootNode() as ShadowRoot;
  const host = root.host as HTMLAhaDialogElement;
  if (!host.persistent && host.maximal) {
    host.emitCancelEvent();
    host.cancelled = true;
    host.hide();
  }
}

export function clearInnerStyles(this: any, e: CustomEvent) {
  // Ensure that we're only resetting the styles if the dialog is closed. This prevents a nested modal from accidentally resetting the styles of its parent modal.
  if (this.open) return;

  // Reset all styles to give us a good starting point.
  this.innerElem?.removeAttribute('style');
  // Re-apply any user-defined styles from the outer <aha-dialog> to the innerElem.
  applyStyle.call(this, inlineStyleToObject(this.host));
}

export function handleAnimationEnd(this: any, event: AnimationEvent) {
  if (event.animationName === 'fadeIn') {
    this.openedEvent.emit(this);
    this.opening = false;
    // Focus on the inner <dialog> element if nothing within the dialog is currently focused.
    if (!this.host.matches(':focus-within')) this.dialogElem.focus();
    if (this.afterFadeIn) this.afterFadeIn();
  } else if (event.animationName === 'fadeOut') {
    // @ts-ignore
    this.dialogElem.close();
    this.closedEvent.emit(this);
    if (this.cancelled) this.cancelledEvent.emit(this);
    this.cancelled = false;
    this.closing = false;
    this.loading = false;

    // Revert styles to "reset" style, and then clear the reset style value.
    this.host.setAttribute('style', this.stylesBeforeOpened);
    this.stylesBeforeOpened = '';
    if (this.afterFadeOut) this.afterFadeOut();
  }
}

/** It is desirable that clicking outside of the "contents" of an open <aha-dialog> that does NOT have a visible backdrop
  will automatically close that <aha-dialog> and any open child <aha-dialog> elements too. Unfortunately the native <dialog>
  element occupies both the entire area of the page and the top-most rendering context of the page, and as such there is 
  no place that is technically "outside" the dialog that is also clickable.
  
  To work around this, we check to see if we are clicking on an <aha-dialog> that has a hidden backdrop. 
  If so, and if the top-most element we've clicked on is the invisible `<dialog>`, and if we've clicked
  outside of the `positionTarget` area, then we know that we've clicked "outside" the dialog contents, 
  and so we can close this dialog.
  
  We've now got another problem. It's also impossible for us to tell what elements were underneath the open 
  <dialog> since <dialog> is in the top-most rendering context of the page. Doing something like 
  `document.elementsFromPoint(x,y)` will only yield the open <dialog> and root <html> elements.
  
  To work around this, we listen for when the bottom-most (as several aha-dialogs may be open simultaneously) <aha-dialog>
  element _without a visible backdrop_ fires its `closed` event, and _then and only then_ do we check for
  `document.elementsFromPoint(x,y)`. Whatever element that is returned is the current top-most element on the page.
  
  But we can't safely click on that top-most element just yet. The element we're clicking on may be a web component
  itself, and as such may have click handlers on elements that exist within its own shadowDOM. It may be one of
  these shadowDOM'd elements that we actually want to click, and so we have to drill into the shadowDOM and get
  any elements at point x,y and then click _that_ element.
*/
export function handleDialogClick(this: any, event: MouseEvent) {
  // If this dialog has a backdrop, then do nothing.
  if (!this.hideBackdrop) return;

  // If the element we are clicking on is not the <dialog> element itself, then do nothing.
  if (event.target !== this.dialogElem) return;

  if (!this.open || !this.hideBackdrop || this.persistent) return;

  let { clientX: x, clientY: y } = event;

  this.host.addEventListener(
    'aha-dialog:closed',
    () => {
      // Create a duplicate event to the original click event. This way we can "forwarded" the original event (along with)
      // x/y coordinates to the element on the page that we _would_ have clicked on had the invisible <dialog> not interfered
      // with the click.
      const forwardedEventOptions = pointerEventOptions();
      for (let key in forwardedEventOptions) {
        // @ts-ignore
        forwardedEventOptions[key] = event[key];
      }

      // Add a new property to the forwarded event, so that it is possible to tell that this is an event that was forwarded
      // rather than a usual event.
      const forwardedEvent = new PointerEvent(event.type, forwardedEventOptions);
      Object.defineProperty(forwardedEvent, 'ahaDialogForwardedEvent', {
        get() {
          return true;
        },
      });

      // Dispatch the forwarded event on the the top-most element.
      let elemUnderneath = document.elementFromPoint(x, y);
      while (elemUnderneath && elemUnderneath.shadowRoot) {
        const nextElemUnderneath = elemUnderneath.shadowRoot.elementFromPoint(x, y);
        if (elemUnderneath === nextElemUnderneath) break;
        elemUnderneath = nextElemUnderneath;
      }

      const positionTarget = this.positionTarget;

      // If user is clicking on the triggering element, then do nothing.
      if (
        !positionTarget ||
        Array.isArray(positionTarget) ||
        positionTarget.contains(elemUnderneath)
      )
        return;

      // Wait a tick, otherwise original dialog that was clicked on becomes briefly visible again.
      queueMicrotask(() => {
        if (elemUnderneath && elemUnderneath.isConnected)
          elemUnderneath.dispatchEvent(forwardedEvent);
      });
    },
    { once: true },
  );

  // Hide the dialog without any closing animation.
  this.hide(true);
}

/** Sometimes we want to know (and react) to the user mousing outside of, or back inside of, the <aha-dialog>'s "inner" 
  contents. This can be tricky because the triggering element (the `positionTarget`) actually lies underneath the open
  <dialog> and as such we can't tell if we are actually mousing over it (as <dialog> prevents us from using `elementAtPoint`
  and similar  to drill into the HTML). To work around this, we calculate the position of the `positionTarget` and determine
  if our pointer's X/Y values are inside it. 
*/
export function handleDialogPointerMove(this: any, event: PointerEvent) {
  // If the element we are moving on is not the <dialog> element itself, then we are still inside the "inner" contents of the dialog.
  if (event.target !== this.dialogElem) {
    this.pointerInsideEvent.emit({ host: this, overInner: true });
    return;
  }

  // If we are mousing over the positionTarget, then we are still inside the "inner" contents of the dialog.
  const positionTarget = this.positionTarget;
  if (positionTarget && !Array.isArray(positionTarget)) {
    let { clientX: x, clientY: y } = event;
    const { left, right, top, bottom } = positionTarget.getBoundingClientRect();
    if (x >= left && x <= right && y >= top && y <= bottom) {
      this.pointerInsideEvent.emit({ host: this, overPositionTarget: true });
      return;
    }
  }

  // We're mousing over an area that is outside both the "inner" contents and any `positionTarget` area.
  this.pointerOutsideEvent.emit(this);
}

// Find the element which is previous to the host element. This will be the automatically set `rel` element (the previous element).
export function getPreviousElementSiblingThatIsDisplayable(elem: Element): any {
  const prevElemSibling = elem.previousElementSibling;
  if (!prevElemSibling) {
    let root = elem.getRootNode() as ShadowRoot;
    if (!root) return;
    let host = root.host as Element;
    if (!host) return;
    return getPreviousElementSiblingThatIsDisplayable(host);
  } else if (nonDisplayableElementTagNames.has(prevElemSibling.tagName)) {
    return getPreviousElementSiblingThatIsDisplayable(prevElemSibling);
  } else {
    return prevElemSibling;
  }
}

export function setPositionTarget(this: any) {
  if (this.positionTarget || this.position !== 'relative') return;

  let positionTarget;

  if (typeof this.rel === 'string') {
    const pointRegex = /^\s*?(\d+)\s*?,\s*?(\d+)\s*?$/;
    const pointMatches = this.rel.match(pointRegex);
    if (pointMatches?.length === 3) {
      positionTarget = [parseFloat(pointMatches[1]), parseFloat(pointMatches[2])];
    } else {
      const closestRegex = /^::closest\((.*?)\)(.*)/;
      const matches = this.rel.match(closestRegex);
      if (matches) {
        let [, closestMatcher, restMatcher] = matches;
        closestMatcher = closestMatcher.trim();
        restMatcher = restMatcher.trim();

        // Use either the native `closest` JS matcher, or, if nothing matches, walk up the light/shadow DOM tree to find it.
        const closestElem = this.host.closest(closestMatcher) || closest(this.host, closestMatcher);

        // If there's no closest element, return nothing.
        if (!closestElem) {
          throw new Error(`Count not find an element that matches "${this.rel}"`);
        }

        if (!restMatcher) {
          // If we only wanted to find the closest element, then just return that.
          positionTarget = closestElem;
        } else {
          // Otherwise find the element that is found inside the closest element.
          const foundElem = closestElem.querySelector(`:scope ${restMatcher}`);
          if (foundElem) {
            positionTarget = foundElem;
          } else {
            throw new Error(`Count not find an element that matches "${this.rel}"`);
          }
        }
      } else {
        if (this.rel) {
          positionTarget = document.querySelector(this.rel);
        }
      }
    }
  } else if (this.rel) {
    positionTarget = this.rel;
  } else {
    positionTarget = getPreviousElementSiblingThatIsDisplayable(this.host);
  }

  this.positionTarget = positionTarget;
}

export function calculateBoundaries(this: any) {
  const notFoundObj = {
    top: undefined,
    bottom: undefined,
    left: undefined,
    right: undefined,
    width: undefined,
    height: undefined,
  };
  const dialogRect = this.innerElem?.getBoundingClientRect() || notFoundObj;
  const boundaryRect = this.dialogElem?.getBoundingClientRect() || notFoundObj;

  const values: Record<string | number | symbol, any> = {
    dialogTop: dialogRect.top,
    dialogBottom: dialogRect.bottom,
    dialogLeft: dialogRect.left,
    dialogRight: dialogRect.right,
    dialogWidth: dialogRect.width,
    dialogHeight: dialogRect.height,
    boundaryTop: boundaryRect.top,
    boundaryBottom: boundaryRect.bottom,
    boundaryLeft: boundaryRect.left,
    boundaryRight: boundaryRect.right,
    boundaryWidth: boundaryRect.width,
    boundaryHeight: boundaryRect.height,
  };

  const allKeysDefined = Object.values(values).every(n => typeof n !== 'undefined');

  values.withinBoundaries =
    allKeysDefined &&
    values.boundaryTop <= values.dialogTop &&
    values.boundaryBottom >= values.dialogBottom &&
    values.boundaryLeft <= values.dialogLeft &&
    values.boundaryRight >= values.dialogRight;

  return values;
}

export function reposition(this: any, ignoredDirections: Set<string> = new Set()) {
  if (!this.isRelativelyPositioned) return;

  const tagName = this.host.tagName.toLowerCase();

  const positionTarget = this.positionTarget;

  if (positionTarget && this.innerElem) {
    const {
      dialogWidth,
      dialogHeight,
      boundaryTop,
      boundaryBottom,
      boundaryLeft,
      boundaryRight,
      boundaryWidth,
      boundaryHeight,
    } = calculateBoundaries.call(this);

    let targetTop;
    let targetBottom;
    let targetLeft;
    let targetRight;
    let targetWidth;
    let targetHeight;

    if (Array.isArray(positionTarget)) {
      // If the positionTarget is an array, then calculate position of dialog relative to this point.
      targetTop = positionTarget[1];
      targetBottom = positionTarget[1];
      targetLeft = positionTarget[0];
      targetRight = positionTarget[0];
      targetWidth = 0;
      targetHeight = 0;
    } else {
      const { top, bottom, left, right, width, height } = positionTarget.getBoundingClientRect();
      targetTop = top;
      targetBottom = bottom;
      targetLeft = left;
      targetRight = right;
      targetWidth = width;
      targetHeight = height;
    }

    let offset = 0;
    if (this.offset) {
      if (typeof this.offset === 'number') {
        offset = this.offset;
      } else {
        offset = parseFloat(this.offset);
      }
    }

    let targetTopOffset = this.offset === 'inset' ? targetBottom : targetTop - offset;
    let targetBottomOffset = this.offset === 'inset' ? targetTop : targetBottom + offset;
    let targetLeftOffset = this.offset === 'inset' ? targetRight : targetLeft - offset;
    let targetRightOffset = this.offset === 'inset' ? targetLeft : targetRight + offset;

    let targetHidden;
    if (this.offset === 'inset') {
      targetHidden =
        boundaryTop > targetTop ||
        boundaryTop > targetBottom ||
        boundaryBottom < targetTop ||
        boundaryBottom < targetBottom ||
        boundaryLeft > targetLeft ||
        boundaryLeft > targetRight ||
        boundaryRight < targetRight ||
        boundaryRight < targetLeft;
    } else {
      targetHidden =
        (boundaryTop > targetTop && boundaryTop > targetBottom) ||
        (boundaryBottom < targetTop && boundaryBottom < targetBottom) ||
        (boundaryLeft > targetLeft && boundaryLeft > targetRight) ||
        (boundaryRight < targetRight && boundaryRight < targetLeft);
    }

    if (targetHidden) {
      this.hide(true);
      return;
    }

    const areas: Record<string | number | symbol, any> = {};
    areas.top = boundaryWidth * targetTopOffset;
    areas.bottom = boundaryWidth * targetBottomOffset;
    areas.left = boundaryHeight * targetLeftOffset;
    areas.right = boundaryHeight * targetRightOffset;
    areas['top-start'] = areas.top - targetTopOffset * targetLeft;
    areas['top-end'] = areas.top - targetTopOffset * targetRight;
    areas['bottom-start'] = areas.bottom - targetBottomOffset * targetLeft;
    areas['bottom-end'] = areas.bottom - targetBottomOffset * targetRight;
    areas['left-start'] = areas.left - targetTop * targetLeftOffset;
    areas['left-end'] = areas.left - targetBottom * targetLeftOffset;
    areas['right-start'] = areas.right - targetTop * targetRightOffset;
    areas['right-end'] = areas.right - targetBottom * targetRightOffset;

    // `sortedAreas` is an array of all directions, sorted by how much area is available within that direction.
    const sortedAreas = Object.entries(areas).sort(function (a: any, b: any) {
      return b[1] - a[1];
    });

    // `bestDirections` is an set of all the directions, in preferred order. That is, the directions
    // we have specified that we want, followed by the directions that have the greatest possible area.
    let bestDirections: Set<any> = new Set(this.possibleDirections);
    if (this.possibleDirections.has('auto')) {
      bestDirections = new Set(this.possibleDirections);
      sortedAreas.forEach(([direction]) => bestDirections.add(direction));
      bestDirections.delete('auto');
    }

    // If someone has specified 'auto', the we sort give them a list of the bestDirections, if it is not 'auto', then give them the specified directions.
    const direction = Array.from(bestDirections || this.possibleDirections).filter(dir => {
      return !ignoredDirections.has(dir);
    })[0];

    this.openedDirection = direction;

    // If there is no possible direction, then do nothing.
    if (!direction) return;

    // CSS properties cannot be set via Object.assign, so we have to set these manually.
    this.innerElem.style.setProperty(`--${tagName}--fade-in-duration`, '0s');
    this.innerElem.style.setProperty(`--${tagName}--fade-out-duration`, '0s');

    const styles: StyleObj = {
      top: '',
      bottom: '',
      left: '',
      right: '',
    };

    if (/^top/.test(direction)) {
      styles.bottom = `${boundaryBottom - targetTopOffset}px`;
    } else if (/^bottom/.test(direction)) {
      styles.top = `${targetBottomOffset}px`;
    } else if (['left-end', 'right-end'].includes(direction)) {
      styles.bottom = `${boundaryBottom - targetBottom}px`;
    } else if (['left-start', 'right-start'].includes(direction)) {
      styles.top = `${targetTop}px`;
    }

    // Set horizontal position.
    if (/^left/.test(direction)) {
      styles.right = `${boundaryWidth - targetLeftOffset}px`;
    } else if (/^right/.test(direction)) {
      styles.left = `${targetRightOffset}px`;
    } else if (['top-start', 'bottom-start'].includes(direction)) {
      styles.left = `${targetLeft}px`;
    } else if (['top-end', 'bottom-end'].includes(direction)) {
      styles.right = `${boundaryWidth - targetRight}px`;
    }

    // Set remainder.
    if (['top', 'bottom'].includes(direction)) {
      styles.left = `${Math.max(0, targetLeft - dialogWidth / 2 + targetWidth / 2)}px`;
    } else if (['left', 'right'].includes(direction)) {
      styles.top = `${Math.max(0, targetTop - dialogHeight / 2 + targetHeight / 2)}px`;
    }

    Object.assign(this.innerElem.style, styles);
    const completelyVisible = calculateBoundaries.call(this).withinBoundaries;
    if (completelyVisible) {
      // CSS properties cannot be set via Object.assign, so we have to set these manually.
      this.innerElem.style.removeProperty(`--${tagName}--fade-in-duration`);
      this.innerElem.style.removeProperty(`--${tagName}--fade-out-duration`);
    } else {
      this.reposition(ignoredDirections.add(direction));
    }
  }
}

export function isRelativelyPositioned(this: any) {
  return this.position === 'relative';
}

export function connectedCallback(this: any) {
  this.scrollEvent = scrollEvent.bind(this);
  this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
  this.handleDialogClick = this.handleDialogClick.bind(this);
  this.handleDialogPointerMove = this.handleDialogPointerMove.bind(this);

  if (this.afterCommonConnectedCallback) this.afterCommonConnectedCallback();
}

export function componentDidLoad(this: any) {
  if (this.position === 'relative') setPositionTarget.call(this);

  buildPossibleDirections.call(this);

  // Watch for size changes to the innerElem.
  if (this.innerElem) resizeObserver.observe(this.innerElem);

  // Ensure that styles that are applied to the outer `<aha-dialog>` are reflected on the `#inner` element.
  styleMutationObs.observe(this.host, { attributes: true, attributeFilter: ['style'] });

  if (this.open) {
    this.handleOpen();
    addScrollListeners.call(this);
  }
}

export function disconnectedCallback(this: any) {
  OpenedDialogs.delete(this.host);
  restrictScroll.delete(this.host);
}

// NOTE - Not able to export the render function here, even with all proper Stencil imports, just fails to compile.
